import qs from 'qs'

// Utility functions
const parse = (str) => qs.parse(str)
const stringify = (obj) => new URLSearchParams(qs.stringify(obj, { arrayFormat: 'brackets' })).toString()
const getLocationPath = () => window.location.pathname
const getSearchParams = () => new URLSearchParams(window.location.search).toString()

// Build search params from various inputs, suitable for URL and storage.
function buildTableSearchParams({
  locationPath = getLocationPath(),
  urlParams = getSearchParams(),
  query = {},
  sort = {},
  exportColumns = {},
  page = null,
  per_page = null,
}) {
  const { sort: sortField, dir: sortDir } = sort
  let params = {}

  if (urlParams && typeof urlParams === 'string') {
    urlParams = parse(urlParams)
  }

  if (Object.keys(urlParams).length > 0 || Object.keys(query).length > 0) {
    params = { ...urlParams, ...{ query } }
  }

  if (exportColumns && Object.keys(exportColumns).length > 0) {
    params.export = { columns: exportColumns }
  }

  if (sortField) {
    params.sort = sortField
    params.dir = sortDir || 'asc'
  } else {
    delete params.sort
    delete params.dir
  }

  if (page) {
    params.page = page
    if (page === 1) {
      delete params.page
    }
  } else {
    delete params.page
  }

  if (per_page && per_page !== 25) {
    params.per_page = per_page
  } else {
    delete params.per_page
  }

  return {
    params,
    searchParams: stringify(params),
    locationPath,
  }
}

// Storage utilities

const STORAGE_TTL = 300000 // 5 minutes

function genStorageKey(locationPath) {
  const key = `${getLocationPath()}::${locationPath}`
  return `__dt:${encode(key)}`
}

function encode(key) {
  const data = new TextEncoder().encode(key)
  const prime = 0x100000001b3n
  const mask64 = (1n << 64n) - 1n
  let h = 0xcbf29ce484222325n
  for (let i = 0; i < data.length; i++) h = ((h ^ BigInt(data[i])) * prime) & mask64
  return h.toString(16).padStart(16, '0')
}

function saveParamsIntoStorage({ locationPath, searchParams, ttl = STORAGE_TTL }) {
  const key = genStorageKey(locationPath)
  const search = parse(searchParams)

  // Cleanup assistant - if no params, remove to reduce storage usage
  if (Object.keys(search).length === 0) {
    sessionStorage.removeItem(key)
    return
  }

  const payload = {
    search,
    expiresAt: ttl > 0 ? Date.now() + ttl : null,
  }
  const raw = JSON.stringify(payload)

  try {
    sessionStorage.setItem(key, raw)
  } catch (e) {
    if (e.name === 'QuotaExceededError') {
      // If quota exceeded, clear all entries with '__dt:' prefix and retry once
      Object.keys(sessionStorage).forEach((k) => {
        if (k.startsWith('__dt:')) {
          sessionStorage.removeItem(k)
        }
      })

      return sessionStorage.setItem(key, raw)
    } else {
      console.error('DynamicTable: saving params into storage failed', e)
    }
  }
}

function fetchParamsFromStorage(locationPath) {
  const key = genStorageKey(locationPath)
  const raw = sessionStorage.getItem(key)

  if (!raw) return {}

  try {
    const payload = JSON.parse(raw)

    // Perform TTL check - remove from storage if expired
    if (payload?.expiresAt && Date.now() > payload.expiresAt) {
      sessionStorage.removeItem(key)
      return {}
    }

    return payload.search || {}
  } catch (e) {
    console.error('DynamicTable: fetching params from storage failed', e)
    return {}
  }
}

// Getters for URL and storage params

function getQueryFromURL() {
  return extractQueryFromParams(getSearchParams())
}

function getSortFromURL() {
  return extractSortFromParams(getSearchParams())
}

function getPageFromURL() {
  return extractPageFromParams(getSearchParams())
}

function getQueryFromStorage(locationPath) {
  return extractQueryFromParams(fetchParamsFromStorage(locationPath))
}

function getSortFromStorage(locationPath) {
  return extractSortFromParams(fetchParamsFromStorage(locationPath))
}

function getPageFromStorage(locationPath) {
  return extractPageFromParams(fetchParamsFromStorage(locationPath))
}

function extractQueryFromParams(params) {
  params = parse(params)
  return params.query || {}
}

function extractSortFromParams(params) {
  params = parse(params)
  if (params.sort) {
    return { sort: params.sort, dir: params.dir }
  }
  return {}
}

function extractPageFromParams(params) {
  params = parse(params)
  if (params.page && params.page > 1) {
    const paging = {}
    paging.page = params.page
    if (params.per_page && params.per_page !== 25) {
      paging.per_page = params.per_page
    }
    return paging
  }
  return {}
}

// Exports

export {
  buildTableSearchParams,
  fetchParamsFromStorage,
  genStorageKey,
  getPageFromStorage,
  getPageFromURL,
  getQueryFromStorage,
  getQueryFromURL,
  getSortFromStorage,
  getSortFromURL,
  saveParamsIntoStorage,
}
